import { Description, Subtitle } from '@snapi/luxury';
import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';
import BioGaia from './images/BioGaia.png';
import BioGaiaDrops from './images/BioGaiaDrops.png';
import BioKult from './images/Bio-Kult.png';
import CulturelleBaby from './images/CulturelleBaby.png';
import CulturelleKids from './images/CulturelleKids.png';
import Prebiotic from './images/prebiotic.png';
import { useToggle } from 'react-use';
import { motion, Variants, AnimatePresence } from 'framer-motion';

const Overlay = {
  visible: { opacity: 0.8 },
  hidden: { opacity: 0 },
};

const ClueVariants: Variants = {
  open: {
    opacity: 1,
    height: 'auto',
    pointerEvents: 'auto',
  },
  closed: {
    opacity: 0,
    height: 0,
    pointerEvents: 'none',
  },
};

const PlayUp = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="11.5" fill="white" stroke="#415783" />
      <path d="M12 9L14.5981 13.5H9.40192L12 9Z" fill="#415783" />
    </svg>
  );
};

const allProbiotics = [
  {
    img: BioGaiaDrops,
    title: 'BioGaia® Protectis® Baby Drops',
    description:
      'BioGaia® Protectis® Baby Drops are probiotic supplements for infants, containing Lactobacillus reuteri Protectis to support digestive health, boost immunity, and relieve common gastrointestinal issues in babies.',
    concerns:
      '"Diarrhea", "Constipation", "Eczema", "Dermatitis", "Colic", "Inflammation", "Allergy", "Asthma", "Dairy Sensitivity", "Gluten Sensitivity"',
    type: 'Drops',
    buttonUrl:
      'https://snapihealth.myshopify.com/cart/42027296194766:1?channel=buy_button',
    ageGroup: [0, 12],
    price: 17.99,
    probiotic: true,
  },
  {
    img: BioKult,
    title: 'Bio-Kult Infantis',
    description:
      'Bio-Kult Infantis is a probiotic supplement for infants and young children that supports digestive health and immunity. It contains seven live bacterial strains to promote a healthy gut and is free from artificial additives.',
    concerns:
      '"Diarrhea", "Constipation", "Eczema", "Dermatitis", "Colic", "Inflammation", "Allergy", "Asthma", "Dairy Sensitivity", "Gluten Sensitivity"',
    type: 'Sachet',
    buttonUrl:
      'https://snapihealth.myshopify.com/cart/42027323195598:1?channel=buy_button',
    ageGroup: [6, 24],
    price: 25.18,
    probiotic: true,
  },
  {
    img: BioGaia,
    title: 'BioGaia® Protectis® Chewable Tablets',
    description:
      'BioGaia® Protectis Chewable Tablets is a probiotic supplement that contains a beneficial probiotic strain called Lactobacillus reuteri Protectis. These chewable tablets are designed to support and maintain a healthy balance of bacteria in the gut.',
    concerns: '"Diarrhea", "Constipation", "Eczema", "Dermatitis", "Colic"',
    type: 'Chewable',
    buttonUrl:
      'https://snapihealth.myshopify.com/cart/42027325489358:1?channel=buy_button',
    ageGroup: [13, 204],
    price: 24.29,
    probiotic: true,
  },
  {
    img: CulturelleBaby,
    title:
      'Culturelle® Baby Immune & Digestive Support Probiotic + Vitamin D Drops',
    description:
      'Culturelle® Baby Immune & Digestive Support Probiotic + Vitamin D Drops: A supplement for infants, containing probiotics to support their immune system and digestion, and vitamin D to support bone development.',
    concerns: '"Eczema", "Dermatitis"',
    type: 'Drops',
    buttonUrl:
      'https://snapihealth.myshopify.com/cart/42027329454286:1?channel=buy_button',
    ageGroup: [0, 12],
    price: 23.99,
    probiotic: true,
  },
  {
    img: CulturelleKids,
    title: 'Culturelle® Kids Purely Probiotics Chewables',
    description:
      'Culturelle® Kids Purely Probiotics Chewables are chewable tablets that contain beneficial probiotic strains to support children`s digestive and immune health. They promote a healthy gut, aid digestion, and reduce discomfort.',
    concerns: '"Diarrhea", "Eczema", "Dermatitis"',
    type: 'Chewable',
    buttonUrl:
      'https://snapihealth.myshopify.com/cart/42027331649742:1?channel=buy_button',
    ageGroup: [37, 144],
    price: 18.5,
    probiotic: true,
  },
];
function getProbiotics(
  allProbiotics: oneProbioticProps[],
  concerns: string[],
  ageTested: number
) {
  const oneProbiotic: oneProbioticProps[] = [];

  if (concerns.length === 0 && ageTested <= 12) {
    oneProbiotic.push(allProbiotics[0]);
  }
  if (concerns.length === 0 && ageTested > 12) {
    oneProbiotic.push(allProbiotics[1]);
  }
  if (ageTested < 0) {
    oneProbiotic.push(allProbiotics[0]);
  }

  // Filter the array based on age group
  const filteredByAge = allProbiotics.filter((probiotic) => {
    const [minAge, maxAge] = probiotic.ageGroup;
    return ageTested >= minAge && ageTested <= maxAge;
  });

  // Sort the filtered array based on the number of coincidences in concerns
  const sortedByConcerns = filteredByAge.sort((a, b) => {
    const aCoincidences = concerns.filter((concern) =>
      a.concerns.includes(concern)
    ).length;
    const bCoincidences = concerns.filter((concern) =>
      b.concerns.includes(concern)
    ).length;
    return bCoincidences - aCoincidences;
  });

  oneProbiotic.push(sortedByConcerns[0]);
  return oneProbiotic;
}

interface oneProbioticProps {
  img: StaticImageData;
  title: string;
  description: string;
  concerns: string;
  type: string;
  buttonUrl: string;
  ageGroup: number[];
  price: number;
  probiotic: boolean;
}

const OneProbiotic = ({
  img,
  title,
  description,
  concerns,
  type,
  buttonUrl,
  price,
  probiotic,
}: oneProbioticProps) => {
  return (
    <div className="bg-white rounded-[20px] flex flex-col md:flex-row shadow-md">
      <div className="border-b-[1px] border-gray-soft flex items-center justify-center py-3 sm:py-4 md:border-b-0 md:flex md:basis-1/2 md:border-r-[1px] xl:hidden">
        <Image src={img} className="rounded-[20px]" width={200} height={190} />
      </div>
      <div className=" hidden  items-center justify-center py-3 border-b-0 xl:flex xl:basis-1/2 xl:border-r-[1px] border-gray-soft">
        <Image src={img} className="rounded-[20px]" width={280} height={254} />
      </div>
      <div className="md:flex md:flex-col md:basis-1/2 lg:py-4 xl:py-5">
        <div className="p-5">
          <h4 className="mb-1">{title}</h4>
          <Description>{description}</Description>
          <div className="h-[1px] bg-body mt-3"></div>
        </div>
        <div className="px-5">
          <div className="flex gap-1 flex-wrap">
            <p className="text-[12px] font-semibold mr-2">Helps with:</p>

            {concerns
              ?.slice(1, -1)
              .split('", "')
              .map((concern) => {
                return (
                  <div className="bg-blue px-4 rounded-[40px] text-[12px] font-medium">
                    {concern}
                  </div>
                );
              })}
          </div>
        </div>
        <div className="flex gap-3 items-center justify-start mt-3 px-5">
          <p className="text-[12px] font-semibold">Product:</p>
          <Subtitle>{type}</Subtitle>
        </div>
        <div className="mb-5 mt-5 px-5">
          <button className="bg-green rounded-[40px] w-full h-[48px] uppercase font-semibold lg:w-[240px]">
            <Link
              className="uppercase text-[14px] font-semibold"
              href={buttonUrl}
            >
              {`buy now - $${price}`}
            </Link>
          </button>
        </div>
      </div>
    </div>
  );
};
interface ReportProbioticProps {
  concerns: string[];
  ageTested: number;
}

export const ReportProbiotic = ({
  concerns,
  ageTested,
}: ReportProbioticProps) => {
  const filteredProbiotics = getProbiotics(allProbiotics, concerns, ageTested);
  const [on, toggle] = useToggle(false);

  return (
    <>
      <AnimatePresence>
        {on && (
          <motion.div
            variants={Overlay}
            transition={{ duration: 0.55 }}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={toggle}
            className="bg-body fixed top-0 bottom-0 left-0 right-0  flex justify-center w-full z-10"
          />
        )}
      </AnimatePresence>

      <div className="bg-blue-light">
        <section className="bg-green-light rounded-t-[40px]">
          <div className="container">
            <div className="">
              <div className="flex flex-col mb-3 lg:mb-0 relative">
                <div className="lg:flex">
                  <h2 className="text-dark lg:flex lg:basis-1/2  mb-5">
                    Probiotic & Prebiotic Plan
                  </h2>
                  <div className="flex gap-3 cursor-pointer">
                    <motion.span
                      className="flex items-start justify-start w-6 h-6 mb-[6px]"
                      animate={on ? 'open' : 'closed'}
                      variants={{
                        open: { rotate: 180 },
                        closed: { rotate: 0 },
                      }}
                      transition={{ duration: 0.6 }}
                      onClick={toggle}
                    >
                      <PlayUp />
                    </motion.span>
                    <p
                      className="underline mb-5 text-purple-dark"
                      onClick={toggle}
                    >
                      Learn more about probiotics and prebiotics
                    </p>
                  </div>
                </div>
                <h4 className="mb-2">Probiotic recommendations</h4>

                <motion.div
                  animate={on ? 'open' : 'closed'}
                  transition={{ duration: 0.6 }}
                  variants={ClueVariants}
                  className="absolute flex flex-col z-40 top-0 mt-36 sml:mt-28 sm:mt-24 lg:mt-10 bg-white rounded-[20px] border-[1px] border-gray-cream text-blue-dark lg:w-1/2 lg:right-0"
                >
                  <p className="mb-3 mt-4 mx-4 font-semibold">
                    What is the difference between prebiotics and probiotics?
                  </p>
                  <p className="mx-4 mb-4 text-[12px]">
                    Foods rich in prebiotics are like super fuel for the good
                    bacteria in your belly. They help these friendly bacteria
                    grow and keep your tummy in balance. Now, here's the cool
                    part: prebiotics are the food, and probiotics are the good
                    guys who eat that food. When probiotics (the good guys) and
                    prebiotics (their yummy food) work together, they can make a
                    big difference in your digestion, your body's defense
                    system, and your overall health. That's why lots of smart
                    healthcare experts say it's a good idea to eat both
                    probiotic and prebiotic foods to keep your tummy buddies
                    happy and healthy!
                  </p>
                </motion.div>
              </div>
              <p className="mb-10 lg:w-1/2">
                Probiotics are like friendly little helpers for your tummy. They
                work to keep your belly healthy and strong by balancing the good
                and bad bacteria in your gut. We've determined the best
                probiotic for your child based on their gut report and health
                information. Note: We always recommend speaking with your
                provider before introducing new supplements!
              </p>
            </div>
            <div>
              {filteredProbiotics.length > 0 ? (
                <OneProbiotic {...filteredProbiotics[0]} />
              ) : null}
            </div>
          </div>
          {ageTested > 11 && (
            <div className="container">
              <h4 className="mt-5 mb-2">Prebiotic recommendations</h4>
              <p className="mb-10 lg:w-1/2">
                Prebiotics aren't alive like probiotics. They're like a meal for
                the good bacteria in your child's gut. We've determined the best
                prebiotic for your child based on their gut report and health
                information. Note: We always recommend speaking with your
                provider before introducing new supplements!
              </p>
              <div className="bg-white rounded-[20px] flex flex-col md:flex-row shadow-md">
                <div className="border-b-[1px] border-gray-soft flex items-center justify-center pt-5 py-3 sm:py-4 md:border-b-0 md:flex md:basis-1/2 md:border-r-[1px] xl:hidden">
                  <Image
                    src={Prebiotic}
                    className="rounded-[20px]"
                    width={200}
                    height={190}
                  />
                </div>
                <div className=" hidden  items-center justify-center py-3 border-b-0 xl:flex xl:basis-1/2 xl:border-r-[1px] border-gray-soft">
                  <Image
                    src={Prebiotic}
                    className="rounded-[20px]"
                    width={280}
                    height={254}
                  />
                </div>
                <div className="md:flex md:flex-col md:basis-1/2 lg:py-4 xl:py-5">
                  <div className="p-5">
                    <h4 className="mb-1">Begin Health Growing Up Prebiotics</h4>
                    <Description>
                      Helps kids’ poop ‘slip out better’. A high quality, gentle
                      blend for regular poopies, predictable potty time and
                      improved gut health for ages 1+.
                    </Description>
                    <div className="h-[1px] bg-body mt-3"></div>
                  </div>
                  <div className="px-5">
                    <div className="flex gap-1">
                      <p className="text-[12px] font-semibold">Helps with:</p>
                      <p className="text-[12px] ">
                        To promote the growth of beneficial bacteria in the gut
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-center justify-start mt-3 px-5">
                    <p className="text-[12px] font-semibold">Product:</p>
                    <Subtitle>Blend</Subtitle>
                  </div>
                  <div className="mb-5 mt-5 px-5">
                    <button className="bg-green rounded-[40px] w-full h-[48px] uppercase font-semibold lg:w-[240px]">
                      <Link
                        className="uppercase text-[14px] font-semibold"
                        href="https://snapihealth.myshopify.com/cart/42225372102862:1?channel=buy_button"
                      >
                        buy now - $39
                      </Link>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
};
